/** CrmNoteMention (Item E, slice E2) — the @mention suggester for the note editor (§7.2a).
 *
 * Typing `@` opens an ENTITY-ONLY lookup (person / organization / opportunity incl. internal deal
 * team — NEVER content, email bodies, or files); selecting inserts a `mention` node with attrs
 * {kind, id, label}. The SERVER (E1 `resync_mentions`) turns those nodes into `source='mention'`
 * crm_note_link rows on every save, filtered through the author's target read-gate — so the suggester
 * is entitlement-scoped BY CONSTRUCTION (one mixed-kind governed endpoint re-gates both global and
 * client-supplied contextual candidates). The popup owns a full search field, so multi-word/pasted
 * names do not depend on TipTap's inline token boundary. `kind='opportunity'` is a display alias the
 * server maps to target_kind='list_entry'. */
import Mention, { type MentionOptions } from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import type { SuggestionKeyDownProps, SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type KeyboardEvent } from 'react';

import { apiFetch } from '../../api/client';
import type { CrmLookupResponse } from '../../types';
import styles from './crm.module.css';

export interface MentionItem {
  kind: string;
  id: number;
  name: string | null;
  detail?: string | null;
  contextual?: boolean;
  reason?: 'exact' | 'prefix' | 'context' | 'text_match';
}

export interface MentionContextTarget {
  kind: 'person' | 'organization' | 'list_entry' | 'opportunity';
  id: number;
  name?: string | null;
}

/** NUX-1 mixed-kind lookup. The server re-gates context and owns canonical dedup + relevance. */
export async function lookupMentionEntities(
  query: string, context: MentionContextTarget[] = [],
): Promise<MentionItem[]> {
  const q = query.trim();
  if (q.length < 2 && context.length === 0) return [];
  const response = await apiFetch<CrmLookupResponse>('/api/crm/notes/mentions/search', {
    method: 'POST',
    body: JSON.stringify({
      q,
      context: context.slice(0, 100).map(({ kind, id }) => ({ kind, id })),
      limit: 20,
    }),
  });
  return (response.results ?? []) as MentionItem[];
}

interface MentionListHandle { onKeyDown: (p: SuggestionKeyDownProps) => boolean }
interface MentionListProps {
  initialQuery: string;
  context: MentionContextTarget[];
  command: (item: MentionItem) => void;
  onCancel: () => void;
}

const MentionList = forwardRef<MentionListHandle, MentionListProps>((props, ref) => {
  const [query, setQuery] = useState(props.initialQuery);
  const [items, setItems] = useState<MentionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const requestRef = useRef(0);
  const contextKey = props.context.map((item) => `${item.kind}:${item.id}`).join('|');

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const seq = ++requestRef.current;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setFailed(false);
      lookupMentionEntities(query, props.context)
        .then((next) => {
          if (requestRef.current !== seq) return;
          setItems(next);
          setSel(0);
        })
        .catch(() => {
          if (requestRef.current !== seq) return;
          setItems([]);
          setFailed(true);
        })
        .finally(() => { if (requestRef.current === seq) setLoading(false); });
    }, 160);
    return () => window.clearTimeout(timer);
  // `contextKey` is the stable semantic dependency; callers may rebuild the array during a save.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, contextKey]);

  const handleKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault(); event.stopPropagation(); props.onCancel(); return;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault(); event.stopPropagation();
      const n = items.length;
      if (n > 0) setSel((s) => event.key === 'ArrowUp' ? (s + n - 1) % n : (s + 1) % n);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault(); event.stopPropagation();
      const item = items[sel];
      if (item) props.command(item);
    }
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      const n = items.length;
      if (n === 0) return false;
      if (event.key === 'ArrowUp') { setSel((s) => (s + n - 1) % n); return true; }
      if (event.key === 'ArrowDown') { setSel((s) => (s + 1) % n); return true; }
      if (event.key === 'Enter') { const it = items[sel]; if (it) props.command(it); return true; }
      return false;
    },
  }), [items, props, sel]);

  const contextual = items.filter((item) => item.contextual);
  const general = items.filter((item) => !item.contextual);
  const renderItems = (group: MentionItem[]) => group.map((it) => {
    const i = items.indexOf(it);
    return (
      <button
        type="button"
        key={`${it.kind}:${it.id}`}
        role="option"
        aria-selected={i === sel}
        className={`${styles.mentionItem} ${i === sel ? styles.mentionItemActive : ''}`}
        onMouseEnter={() => setSel(i)}
        onMouseDown={(e) => { e.preventDefault(); props.command(it); }}
      >
        <span className={styles.mentionKind}>{it.kind === 'opportunity' ? 'deal' : it.kind}</span>
        <span className={styles.mentionResultText}>
          <span>{it.name}</span>
          {it.detail ? <span className={styles.mentionDetail}>{it.detail}</span> : null}
        </span>
        {it.contextual && <span className={styles.mentionContext}>On this record</span>}
      </button>
    );
  });

  return (
    <div className={styles.mentionPopup}>
      <div className={styles.mentionSearchWrap}>
        <input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKey} className={styles.mentionSearch}
          aria-label="Search people, organizations, and deals"
          placeholder="Search a full name or organization…" />
        <span className={styles.mentionSearchHint}>Type spaces freely</span>
      </div>
      {loading && <div className={styles.mentionEmpty} role="status">Searching…</div>}
      {!loading && failed && (
        <div className={styles.mentionEmpty} role="alert">Search failed — keep typing or try again.</div>
      )}
      {!loading && !failed && items.length === 0 && (
        <div className={styles.mentionEmpty}>No matches</div>
      )}
      {!loading && !failed && items.length > 0 && (
        <div className={styles.mentionResults} role="listbox" aria-label="Mention results">
          {contextual.length > 0 && (
            <>
              <div className={styles.mentionSectionLabel}>Suggested for this record</div>
              {renderItems(contextual)}
            </>
          )}
          {general.length > 0 && (
            <>
              <div className={styles.mentionSectionLabel}>All results</div>
              {renderItems(general)}
            </>
          )}
        </div>
      )}
    </div>
  );
});
MentionList.displayName = 'MentionList';

/** The Mention extension configured with the entity suggester. `kind` is added as a node attribute
 * (persisted into body_json for the server's mention→link sync). */
export function createMentionExtension(context: MentionContextTarget[] = []) {
  const base = Mention.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        kind: {
          default: null,
          parseHTML: (el: HTMLElement) => el.getAttribute('data-kind'),
          renderHTML: (attrs: { kind?: string | null }) => (attrs.kind ? { 'data-kind': attrs.kind } : {}),
        },
      };
    },
  });
  // Build the suggestion internally-typed on MentionItem, then cast once at the configure boundary
  // (TipTap's Mention hardcodes the suggestion item type to the node attrs, so the generic can't flow).
  const suggestion: Omit<SuggestionOptions<MentionItem>, 'editor'> = {
    char: '@',
    // The popup owns a real search input. Returning no inline items prevents TipTap's single-token
    // query boundary from becoming the product's search contract.
    items: () => [],
    command: ({ editor, range, props }) => {
      editor.chain().focus().insertContentAt(range, [
        { type: 'mention', attrs: { id: props.id, label: props.name, kind: props.kind } },
        { type: 'text', text: ' ' },
      ]).run();
    },
    render: () => {
      let renderer: ReactRenderer<MentionListHandle, MentionListProps> | null = null;
      let box: HTMLDivElement | null = null;
      let closed = false;
      const cleanup = () => {
        renderer?.destroy(); box?.remove(); renderer = null; box = null; closed = true;
      };
      const place = (clientRect: SuggestionProps<MentionItem>['clientRect']) => {
        const r = clientRect?.();
        if (!box || !r) return;
        box.style.position = 'fixed';
        const width = Math.min(440, window.innerWidth - 24);
        box.style.width = `${width}px`;
        box.style.left = `${Math.max(12, Math.min(r.left, window.innerWidth - width - 12))}px`;
        box.style.top = `${r.bottom + 4}px`;
        box.style.zIndex = '70';
        requestAnimationFrame(() => {
          if (!box) return;
          const height = box.getBoundingClientRect().height;
          if (r.bottom + 4 + height > window.innerHeight - 12 && r.top - height - 4 >= 12) {
            box.style.top = `${r.top - height - 4}px`;
          }
        });
      };
      return {
        onStart: (p) => {
          closed = false;
          renderer = new ReactRenderer(MentionList, {
            props: {
              initialQuery: p.query,
              context,
              command: (it: MentionItem) => p.command(it),
              onCancel: () => { cleanup(); p.editor.commands.focus(); },
            }, editor: p.editor,
          });
          box = document.createElement('div');
          box.appendChild(renderer.element);
          document.body.appendChild(box);
          place(p.clientRect);
        },
        onUpdate: (p) => {
          if (closed) return;
          renderer?.updateProps({
            initialQuery: p.query,
            context,
            command: (it: MentionItem) => p.command(it),
            onCancel: () => { cleanup(); p.editor.commands.focus(); },
          });
          place(p.clientRect);
        },
        onKeyDown: (p) => {
          if (p.event.key === 'Escape') return true;
          return renderer?.ref?.onKeyDown(p) ?? false;
        },
        onExit: cleanup,
      };
    },
  };
  return base.configure({
    HTMLAttributes: { class: styles.mentionNode },
    renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
    suggestion: suggestion as unknown as MentionOptions['suggestion'],
  });
}
